import { computed, effect, Injectable, inject, signal, untracked, WritableSignal } from "@angular/core";
import { ContentBlockParserService } from "./content-block-parser.service";
import { BackgroundJobTrackerService } from "./background-job-tracker.service";
import { PlanBlockStoreService } from "./plan-block-store.service";
import { AiChatSessionService } from "./ai-chat-session.service";
import { AgentFactory, ChatContextService } from "../../../agent-ui";
import type { NormalizedContentBlock } from "../model/block.types";

/** Shared empty result for unknown message lookups — one stable reference, never reallocated. */
const EMPTY_BLOCKS: readonly NormalizedContentBlock[] = [];

/**
 * Stream-layer reducer of AG-UI `STATE_SNAPSHOT` events into per-message accumulated content
 * blocks (chat-snapshot-stream-reducer S3). This is the ORIGIN fix for the NG0103 infinite
 * change-detection loop: the snapshot → blocks reduction used to run inside
 * ContentDispatcherComponent's constructor effect, but Angular 21 (zoneless) executes component
 * effects INSIDE the change-detection cycle, and the effect's reactive coupling with the stores
 * it fed self-notified → re-ran → looped. Here the reduction runs in a plain rxjs callback
 * (the agent event stream) — never inside a component effect — following the app's data-flow
 * convention: custom events → stream callbacks → stores → `computed()` for views.
 *
 * Mirrors `PlanBlockStoreService`'s hardened shape: provided per chat-container (NOT root) so
 * it resets with the container, per-key `WritableSignal` map + a key-universe signal,
 * identity-guarded value writes (a stored block array is only replaced when its reference
 * changes), and `store()/clear()`. The dispatcher is a pure READER of this store.
 */
@Injectable()
export class ContentBlockStreamService {
  /**
   * Key universe of stored message ids — the reactive anchor for "the message appears/clears".
   * Only bumped when the key set itself changes (new id inserted, store cleared), so a reader
   * of one existing message is never re-notified by an unrelated insert (per-key skip).
   */
  readonly #messages = signal<Set<string>>(new Set());

  /** Per-message signal of accumulated blocks. The container Map is plain (non-reactive):
   *  ids add/remove go through `#messages`, value writes go through each message's own signal. */
  readonly #blocks = new Map<string, WritableSignal<NormalizedContentBlock[] | null>>();

  /**
   * First message (in arrival order) that carried each block id — a user's uploaded file is
   * promoted to evidence on their own message, and the turn's final answer carries it again
   * as an input of the plan. Reads the key universe plus every message signal, so it
   * recomputes when a message appears or its blocks change.
   */
  readonly #owners = computed(() => {
    this.#messages();
    const owners = new Map<string, string>();
    for (const [messageId, sig] of this.#blocks) {
      for (const block of sig() ?? EMPTY_BLOCKS) {
        if (!owners.has(block.id)) owners.set(block.id, messageId);
      }
    }
    return owners;
  });

  readonly #parser = inject(ContentBlockParserService);

  /**
   * Durable plan-block store (doc 31 §7.5 join anchor). Optional-injected because it is provided
   * per chat-container — contexts without a container (tests, debug tooling) simply don't feed it.
   */
  readonly #planBlockStore = inject(PlanBlockStoreService, { optional: true });

  readonly #tracker = inject(BackgroundJobTrackerService);

  readonly #chatContext = inject(ChatContextService);

  readonly #session = inject(AiChatSessionService);

  /**
   * Shared agent store signal (via AgentFactory) — the hydration source. Optional like the plan
   * store: stripped contexts without the factory (some test harnesses) simply skip hydration.
   */
  readonly #agentFactory = inject(AgentFactory, { optional: true });

  constructor() {
    const agentStore = this.#agentFactory?.getSharedStoreSignal(
      computed(() => this.#chatContext.agentId() ?? undefined),
    );
    if (!agentStore) return;

    // Hydration + unified feed (restored-threads fix): the shared AgentStore holds the agent
    // state for BOTH live snapshots (AgentStore.onStateChanged) and restored threads
    // (AiChatSessionService.seedForThread → seedMessages), so watching it covers every source
    // of block data through the exact same store() path — a restored conversation hydrates
    // without any run event, and refreshThread re-hydrates after background-job completion.
    //
    // NG0103 invariant, enforced structurally (never by convention): this effect reads ONLY
    // `agentStore().state()`; every write in store() happens inside untracked and none of them
    // touches agent state, so the effect's own dependencies cannot be re-notified by its own
    // writes. Re-fed identical blocks are absorbed by the identity-guarded merge (no write,
    // no notify) — double-feeding from the live onStateSnapshotEvent wiring is a no-op.
    effect(() => {
      const state = agentStore()?.state?.();
      if (state != null) this.store(state);
    });
  }

  private entry(
    messageId: string,
    initial?: NormalizedContentBlock[] | null,
  ): WritableSignal<NormalizedContentBlock[] | null> {
    let sig = this.#blocks.get(messageId);
    if (!sig) {
      sig = signal<NormalizedContentBlock[] | null>(initial ?? null);
      this.#blocks.set(messageId, sig);
    }
    return sig;
  }

  /**
   * Reduces one raw AG-UI `onStateSnapshotEvent` payload into per-message accumulated blocks.
   * `event` is the `{ type: "STATE_SNAPSHOT", snapshot }` envelope; a bare snapshot (no
   * `snapshot` key) is accepted too. Per message (S2 semantics, identical to the former
   * dispatcher effect):
   * - a parse returning zero blocks keeps the previous accumulated blocks (S2.b);
   * - duplicate block ids inside one parse: last occurrence wins (S2.c);
   * - grow-only merge by block id — replace only when `status` or `data` reference changed,
   *   append unseen ids, never remove (S2.a);
   * - `plan` blocks feed `PlanBlockStoreService.store()` (S2.d) and `background_job` blocks
   *   call `BackgroundJobTrackerService.startJob(data, onComplete)` whose completion refreshes
   *   the parent thread (S2.e) — both driven by the stores' own internal guards/idempotence.
   *
   * Runs in a plain rxjs callback — NEVER inside a component effect. The body is wrapped in
   * `untracked()` with the same rule as `PlanBlockStoreService.store()`: a write call must not
   * create a reactive coupling with its caller; per-message reads below guard the writes, and
   * untracked removes ONLY the caller-side subscription — store readers keep reactivity.
   */
  store(event: unknown): void {
    const snap = (event as { snapshot?: unknown } | null | undefined)?.snapshot ?? event;
    const byMessage = this.#parser.parseBlocksByMessage(snap);
    untracked(() => {
      let keyAdded = false;
      for (const [messageId, parsedRaw] of byMessage) {
        // S2.b: an empty parse never wipes the blocks already accumulated for the message.
        if (parsedRaw.length === 0) continue;

        // S2.c: the backend can emit the same block id twice in one snapshot (duplicate
        // evidence entries). Keep only the last occurrence so the merge below never has to
        // choose between two live objects for the same id.
        const parsed = [...new Map(parsedRaw.map((b) => [b.id, b])).values()];

        // S2.d: feed the durable plan-block store — the store filters `kind === "plan"` and
        // applies the last-wins dedup by plan_ref itself; raw blocks only, no transformation.
        if (parsed.some((b) => b.kind === "plan")) {
          this.#planBlockStore?.store(parsed);
        }

        // S2.e: start polling for any background_job blocks not yet tracked. startJob is
        // internally idempotent; the completion callback refreshes the parent thread.
        for (const b of parsed) {
          if (b.kind === "background_job") {
            this.#tracker.startJob(b.data, () => {
              const agentId = this.#chatContext.agentId();
              const threadId = this.#chatContext.threadId();
              if (agentId && threadId) {
                this.#session.refreshThread(agentId, threadId);
              }
            });
          }
        }

        const existing = this.#blocks.get(messageId);
        if (!existing) {
          // First appearance: create the entry already holding the blocks, then bump the universe.
          this.entry(messageId, parsed);
          keyAdded = true;
        } else {
          const current = existing();
          const merged = this.#merged(current ?? [], parsed);
          // Identity guard: write only when the accumulated array reference actually changed —
          // re-fed identical snapshots must not notify readers (per-key skip).
          if (merged !== null) existing.set(merged);
        }
      }
      if (keyAdded) this.#messages.set(new Set(this.#blocks.keys()));
    });
  }

  /**
   * Accumulated blocks of one message, or [] when the id is unknown. Reactive lookup mirroring
   * `PlanBlockStoreService.byPlanRef`: unknown ids read the `#messages` universe so a later
   * appearance (or a clear) re-runs the reader; known ids read only their own signal, so an
   * unrelated message's update never re-notifies them.
   */
  blocksFor(messageId: string): readonly NormalizedContentBlock[] {
    const sig = this.#blocks.get(messageId);
    if (sig) return sig() ?? EMPTY_BLOCKS;
    // Unknown id: read the universe so a later appearance re-runs this reader.
    this.#messages();
    return EMPTY_BLOCKS;
  }

  /** Id of the message that first carried `blockId`, or null when no message has it. */
  ownerOf(blockId: string): string | null {
    return this.#owners().get(blockId) ?? null;
  }

  /**
   * Empties the store. For agent-switch/teardown symmetry only — blocks are durable per
   * message and are NOT cleared on submit. Nulls every per-message signal (notifying its
   * readers), clears the map and resets the universe so unknown-id readers re-check.
   */
  clear(): void {
    for (const sig of this.#blocks.values()) sig.set(null);
    this.#blocks.clear();
    this.#messages.set(new Set());
  }

  /**
   * Grow-only merge of `parsed` into `current` (S2.a) — exact port of the former dispatcher
   * effect: replace a stored block only when `status` or `data` reference changed, append
   * unseen ids, never remove. Returns null when nothing changed (no new array allocated).
   */
  #merged(current: NormalizedContentBlock[], parsed: NormalizedContentBlock[]): NormalizedContentBlock[] | null {
    const indexById = new Map(current.map((b, i) => [b.id, i]));
    const merged = [...current];
    let changed = false;

    for (const b of parsed) {
      const idx = indexById.get(b.id);
      if (idx !== undefined) {
        if (merged[idx].status !== b.status || merged[idx].data !== b.data) {
          merged[idx] = b;
          changed = true;
        }
      } else {
        merged.push(b);
        changed = true;
      }
    }

    return changed ? merged : null;
  }
}
